import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Box, Button, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api/apiClient';
import { WarningIcon, VerifiedCheckIcon } from '../utils/icons';

// ── Layout ────────────────────────────────────────────────────────────────────

// <aside role="status"> is the correct landmark + live-region combination for
// a *persistent* notification that lives across navigations. The previous
// role="alert" caused the banner to be re-announced assertively on every
// page transition, which is disruptive for screen reader users — the message
// hasn't changed, only the surrounding page has.
//
// Background and text use the chip token pair (--color-bg-warning +
// --color-chip-text-warning) so the banner clears AAA in both light and
// dark mode automatically.
const Banner = styled('aside')`
  background: var(--color-bg-warning);
  color: var(--color-chip-text-warning);
  border-bottom: 1px solid var(--color-chip-text-warning);
  padding: 10px var(--spacing-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 14px;
`;

const BannerActions = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(false);

  if (!user || user.emailVerified || dismissed) return null;

  const handleResend = async () => {
    setLoading(true);
    setError('');
    try {
      await apiClient.post('/auth/resend-verification');
      setSent(true);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Failed to resend. Try again shortly.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Banner role="status" aria-live="polite">
      {/* Typography keeps text in a semantic <span> rather than a bare text node */}
      <Typography component="span" variant="body2" sx={{ color: 'inherit' }}>
        <WarningIcon sx={{ fontSize: 16, verticalAlign: '-3px', mr: '6px' }} aria-hidden="true" />
        Please verify your email address to unlock all features.{' '}
        <Link
          to="/verify-email"
          style={{
            color: 'inherit',
            fontWeight: 600,
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          Resend or learn more
        </Link>
      </Typography>

      <BannerActions>
        {error && (
          <Typography component="span" variant="caption" sx={{ color: 'var(--color-text-error)' }}>
            {error}
          </Typography>
        )}

        {sent ? (
          <Typography
            component="span"
            variant="body2"
            sx={{ color: 'var(--color-text-success)', fontWeight: 500 }}
          >
            <VerifiedCheckIcon
              sx={{ fontSize: 14, verticalAlign: '-2px', mr: '4px' }}
              aria-hidden="true"
            />
            Check your inbox
          </Typography>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={handleResend}
            disabled={loading}
            disableElevation
            aria-busy={loading || undefined}
            sx={{
              background: 'var(--color-primary)',
              color: 'var(--color-text-inverse)',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              borderRadius: 'var(--radius-sm)',
              padding: '5px 14px',
              textTransform: 'none',
              '&:hover:not(:disabled)': { opacity: 0.85, background: 'var(--color-primary)' },
              '&:focus-visible': {
                outline: '2px solid var(--color-border-focus)',
                outlineOffset: 2,
              },
            }}
          >
            {loading ? 'Sending…' : 'Resend'}
          </Button>
        )}

        {/* IconButton gives a proper button role, focus ring, and 44×44 touch target.
            Use textSecondary (not textDisabled) so the close icon meets contrast. */}
        <IconButton
          aria-label="Dismiss email verification banner"
          size="small"
          onClick={() => setDismissed(true)}
          sx={{
            color: 'inherit',
            opacity: 0.75,
            '&:hover': { opacity: 1 },
            '&:focus-visible': {
              outline: '2px solid currentColor',
              outlineOffset: 2,
            },
          }}
        >
          <CloseIcon fontSize="small" aria-hidden="true" />
        </IconButton>
      </BannerActions>
    </Banner>
  );
}
